import { AuthChannelType } from "@/generated/graphql";
import { toValue, watch, type MaybeRefOrGetter, computed } from "vue";
import {
  useDumbStorage,
  type DumbStorageSerializer,
} from "@/composables/use-dumb-storage";
import { z } from "zod/v4";
import { setAuthHeaders } from "@/utils/headers";

type TokenStorageOptions = MaybeRefOrGetter<{
  supportedAuthChannels: MaybeRefOrGetter<AuthChannelType[]>;
}>;

const storedTokenSchema = z.object({
  jwt: z.string(),

  /**
   * Whether the token matches the identity of a user.
   * This is used to determine if the token is valid for the current user.
   */
  matchesIdentity: z.boolean(),
  authChannel: z.enum(AuthChannelType),
  timestamp: z.number(), // To track the most recent token
});

type StoredToken = z.infer<typeof storedTokenSchema>;

/**
 * Composable for managing authentication tokens across different auth channels
 */
export function useTokenStorage(
  options: TokenStorageOptions,
  storage: Storage = localStorage,
) {
  const allTokens = useDumbStorage(
    "fanfest:identity:token-map",
    {} as Partial<Record<AuthChannelType, StoredToken>>,
    {
      serializer: new TokenStoreSerializer(),
      storage,
    },
  );

  /**
   * Gets the most recent valid token for a specific auth channel
   */
  const getTokenForChannel = (
    authChannel: AuthChannelType,
  ): StoredToken | null => {
    const tokens = toValue(allTokens);
    return tokens[authChannel] || null;
  };

  /**
   * Computed property that returns the most recent valid token from supported channels
   */
  const mostRecentToken = computed(() => {
    const supportedChannels = toValue(toValue(options).supportedAuthChannels);
    const tokens = toValue(allTokens);

    let mostRecentToken: StoredToken | null = null;
    let mostRecentTimestamp = 0;

    for (const channel of supportedChannels) {
      const token = tokens[channel];
      if (token && token.timestamp > mostRecentTimestamp) {
        mostRecentToken = token;
        mostRecentTimestamp = token.timestamp;
      }
    }

    return mostRecentToken;
  });

  /**
   * Stores a token for a specific auth channel
   */
  function storeToken(token: Omit<StoredToken, "timestamp">) {
    const tokens = toValue(allTokens);
    const updatedTokens = {
      ...tokens,
      [token.authChannel]: {
        ...token,
        timestamp: Date.now(),
      },
    };
    allTokens.value = updatedTokens;
  }

  function updateTokenForChannel(
    token: Partial<StoredToken>,
    authChannel: AuthChannelType,
  ) {
    const existingToken = getTokenForChannel(authChannel);
    if (!existingToken) {
      throw new Error("Token not found");
    }

    const updatedToken = { ...existingToken, ...token };
    storeToken(updatedToken);
  }

  /**
   * Clears a specific auth channel token
   */
  function clearTokenForChannel(authChannel: AuthChannelType) {
    const tokens = toValue(allTokens);
    const { [authChannel]: _removed, ...remainingTokens } = tokens;
    allTokens.value = remainingTokens;
  }

  /**
   * Clears all stored tokens
   */
  function clearAllTokens() {
    allTokens.value = {};
  }

  function setAuthHeadersFromToken(token: StoredToken | null) {
    if (!token || !token.matchesIdentity) {
      setAuthHeaders(null);
      return;
    }

    setAuthHeaders({
      jwt: token.jwt,
      authChannel: token.authChannel,
    });
  }

  setAuthHeadersFromToken(mostRecentToken.value);

  watch(
    mostRecentToken,
    (value) => {
      setAuthHeadersFromToken(value);
    },
    { flush: "pre" },
  );

  return {
    mostRecentToken,
    getTokenForChannel,
    storeToken,
    updateTokenForChannel,
    clearTokenForChannel,
    clearAllTokens,
    allTokens,
  };
}

class TokenStoreSerializer implements DumbStorageSerializer<
  Partial<Record<AuthChannelType, StoredToken>>
> {
  read(value: string | null) {
    if (!value) return {};

    try {
      const parsed = JSON.parse(value);
      const result: Partial<Record<AuthChannelType, StoredToken>> = {};

      // Validate each token in the object
      for (const [key, tokenValue] of Object.entries(parsed)) {
        if (Object.values(AuthChannelType).includes(key as AuthChannelType)) {
          result[key as AuthChannelType] = storedTokenSchema.parse(tokenValue);
        }
      }

      return result;
    } catch {
      return {};
    }
  }

  write(value: Partial<Record<AuthChannelType, StoredToken>>) {
    return JSON.stringify(value);
  }
}

export type { StoredToken };
